<template>
  <TheTemplateM class="p-the-page-m02">
    <template #heading>プロフィール編集</template>

    <template #body>
      <div class="p-the-page-m02__form c-form-inner">
        <form
          ref="form"
          action="/my-page/profile-update-complete.html"
          method="put"
          @submit.prevent="submit"
        >
          <input v-model="id" type="hidden" name="id" />
          <input v-model="icon" type="hidden" name="icon" />

          <div class="p-the-page-m02__icon">
            <label for="icon" class="p-the-page-m02__icon__label">
              <IconUserImage
                :size="120"
                class="p-the-page-m02__icon_user_image"
              >
                <img
                  :src="iconPreview"
                  :alt="''"
                  class="p-the-page-m02__icon__img"
                />
                <button
                  type="button"
                  for="icon"
                  class="p-the-page-m02__icon_user_image__button"
                >
                  <i class="p-the-page-m02__icon_user_image__button__icon">
                    <IconBase :width="15" :height="15" view-box="0 0 15 15">
                      <IconCamera />
                    </IconBase>
                  </i>
                  <span class="p-the-page-m02__icon_user_image__button__text"
                    >写真を変更</span
                  >
                </button>
              </IconUserImage>
              <input
                id="icon"
                type="file"
                name="icon"
                class="p-the-page-m02__icon_user_image__input_file"
                @change="selectedIconFile"
              />
            </label>
          </div>

          <div class="p-form-group" v-if="bioField">
            <FormLabel for="bio">自己紹介</FormLabel>
            <InputTextarea
              id="bio"
              v-model="bioField.value"
              name="bio"
              :error="bioField.errorMessage"
            />
            <ValidationErrorText v-show="bioField.errorMessage">
              {{ bioField?.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-button-group">
            <div class="p-form-button-group__vertical">
              <ButtonMain
                type="submit"
                :disabled="!form?.meta?.invalid"
                @click-button="updateProfile"
                >保存</ButtonMain
              >
            </div>
            <div class="p-form-button-group__vertical">
              <ButtonMain :tag="'a'" href="/my-page/" :kind="'cancel'"
                >キャンセル</ButtonMain
              >
            </div>
          </div>
        </form>
      </div>
    </template>
  </TheTemplateM>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { useForm, useField, FormContext } from "vee-validate";
import * as yup from "yup";

import TheTemplateM from "@/general/components/templates/TheTemplateM.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconUserImage from "@/common/components/icons/IconUserImage.vue";
import IconCamera from "@/common/components/icons/IconCamera.vue";
import InputTextarea from "@/common/components/atoms/InputTextarea.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";

import commons from "@/common/store/modules/commons";
import { patchCustomUserDetail } from "@/general/store/api";
import globals from "@/general/store/modules/globals";
import LocalStorage from "@/general/utils/storage";
import AuthStorage from "@/general/utils/auth";
import AuthApi from "@/general/api/authApi";

@Options({
  props: {
    id: { type: Number, default: 1 }
  },
  components: {
    TheTemplateM,
    IconBase,
    IconUserImage,
    IconCamera,
    InputTextarea,
    ButtonMain,
    FormLabel,
    ValidationErrorText
  }
})
export default class ProfileUpdate extends Vue {
  id!: number;
  icon: File | null = null;
  iconPreview = "";

  form!: FormContext;
  bioField!: ReturnType<typeof useField>;

  breadcrumb = [
    { text: "", url: "/home/", pageType: "home" },
    { text: "マイページ", url: "/my-page/", pageType: "lower" },
    { text: "プロフィール編集", url: "/", pageType: "lower" }
  ];

  mounted() {
    AuthStorage.checkLogin();
  }

  async created() {
    // バリデーションスキーマの定義
    const schema = yup.object({
      bio: yup
        .string()
        .max(
          256,
          ({ max }: { max: number }) =>
            `自己紹介は${max}文字以内で入力してください。`
        )
    });

    this.form = useForm({ validationSchema: schema });
    this.bioField = useField<string>("bio");

    // ユーザー情報の取得
    await commons.setOrgPageSpecificDataFromLocalStorage();
    const userDetails = JSON.parse(
      LocalStorage.getItem("userDetails") as string
    );
    this.id = userDetails.id;
    this.bioField.value.value = userDetails.bio ?? "";

    await AuthApi.getICSession()
      .then((result) => {
        const ic_session = JSON.parse(result);
        this.iconPreview = ic_session.avatar;
      })
      .catch(() => {
        this.iconPreview = "";
      });

    await commons.getBreadcrumb(this.breadcrumb);
  }

  selectedIconFile(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      this.icon = target.files[0];
      this.iconPreview = URL.createObjectURL(this.icon);
    }
  }

  async updateProfile() {
    const response = await patchCustomUserDetail(
      this.id,
      (this.bioField.value.value as string) || undefined,
      this.icon || undefined
    );

    if (response) {
      window.location.href = "/my-page/profile-update-complete.html";
    }
  }

  async submit() {
    if (!this.form) return;

    const onSubmit = this.form.handleSubmit(async (values) => {
      const response = await patchCustomUserDetail(
        this.id,
        values.bio ?? undefined,
        this.icon ?? undefined
      );

      if (response) {
        window.location.href = "/my-page/profile-update-complete.html";
      }
    });

    await onSubmit();
  }

  get isSp() {
    return globals.isLargeBreakpointWidthOfSp;
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-m02 {
  &__icon {
    margin-bottom: rem(24);
    display: flex;
    justify-content: center;

    &__label {
      width: rem(120);
      height: rem(120);
      border-radius: 50%;
      overflow: hidden;
      cursor: pointer;
    }

    &__img {
      width: 100%;
      display: block;
    }
  }

  &__icon_user_image {
    position: relative;
    pointer-events: none;

    &__button {
      color: $common-white;
      font-size: rem(12);
      font-weight: bold;
      text-align: center;
      position: absolute;
      bottom: 0;
      width: 100%;
      padding: rem(4) 0 rem(10) 0;
      background: rgba(0, 0, 0, 0.6);

      &__icon {
        width: px(15);
        height: px(15);
        margin: auto;
        display: block;
      }

      &__text {
        line-height: 1.5;
        display: block;
      }
    }

    &__input_file {
      display: none;
    }
  }
}
</style>
